<template>
  <div>
    <OverviewComponent
      icon="mdi-clipboard-alert-outline"
      :subtitle="subtitle"
      :headers="headers"
      :data="data"
      :loading="loading"
      :totalPages="totalPages"
      :totalItemCount="totalItemCount"
      titleKey="lineName"
      deadlineKey="deadLine"
      :defaultSortBy="defaultSortBy"
      :defaultSortDesc="defaultSortDesc"
      @setHeader="setHeader"
      @fetchData="fetchData"
      @setColor="setColor"
      @click="click"
    >
      <template v-slot:chips="{ item }">
        <div v-for="(chip, index) in getChips(item)" :key="index">
          <v-chip class="mr-2" :color="chip.color" small>
            {{ chip.title }}
          </v-chip>
        </div>
      </template>

      <template v-slot:underLeft="{ item }">
        {{ item.projectName }}
        {{ item.projectName && item.projSectionApartNum ? "-" : "" }}
        {{ item.projSectionApartNum }}
      </template>

      <template v-slot:underRight="{ item }">
        {{ item.contactPersonName }}
      </template>

      <!-- Custom filter -->
      <template v-slot:filter.projectName="{ head, isTable }">
        <v-autocomplete
          v-if="!head.removeFilter"
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="isTrysilhus ? projects : projects"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : head.text"
          :placeholder="isTable ? 'Søk...' : ''"
          item-text="name"
          item-value="id"
          clearable
          :disabled="head.disabled"
          hide-details="auto"
          menu-props="auto"
          multiple
        >
          <template v-slot:selection="{ item, index }">
            <span v-if="index === 0">{{ item.name }}</span>
            <span v-if="index === 1" class="grey--text text-caption">
              &nbsp;(+{{ head.filterValues.length - 1 }} andre)
            </span>
          </template>
        </v-autocomplete>
      </template>

      <template v-slot:filter.regStatus="{ head, isTable }">
        <v-select
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="regStatuses"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Status'"
          :placeholder="isTable ? 'Velg' : ''"
          item-text="name"
          item-value="id"
          clearable
          hide-details="auto"
          multiple
        >
          <template v-slot:selection="{ item, index }">
            <span v-if="head.filterValues && head.filterValues.length === 1">
              {{ item.name }}
            </span>
            <span v-if="index === 1">
              {{ head.filterValues.length }} valgt
            </span>
          </template>
        </v-select>
      </template>

      <template v-slot:filter.contactPersonName="{ head, isTable }">
        <v-autocomplete
          v-if="!head.removeFilter"
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="isTrysilhus ? contactPersons : contactPersonContact"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Ansvarlig'"
          :placeholder="isTable ? 'Ansvarlig' : ''"
          item-text="name"
          item-value="id"
          :disabled="head.disabled"
          hide-details="auto"
          multiple
        >
          <template v-slot:selection="{ item, disabled, index }">
            <div
              v-if="typeof head.filterValues === 'object'"
              class="v-select__selection v-select__selection--comma"
            >
              {{
                head.filterValues && head.filterValues.length === 1
                  ? item.name
                  : index === 1
                  ? `${head.filterValues.length} valgt`
                  : ""
              }}
            </div>
            <div
              v-else
              class="v-select__selection v-select__selection--comma"
              :class="{ 'v-select__selection--disabled': disabled }"
            >
              {{ item.name }}
            </div>
          </template>

          <template v-slot:item="{ active, item, attrs, on }">
            <v-list-item
              v-on="on"
              v-bind="attrs"
              #default="{ active }"
              :disabled="
                !isTrysilhus &&
                attrs.inputValue &&
                head.filterValues.length === 1
              "
            >
              <v-list-item-action>
                <v-checkbox :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </template>
    </OverviewComponent>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import { HeaderObject } from "@/types/tableInterface";
import complaintApi from "@/api/complaintApi";

import OverviewComponent from "@/components/shared/overview/OverviewComponent.vue";

export default Vue.extend({
  data: () => ({
    data: [] as Array<any>,
    headers: [] as Array<HeaderObject>,
    loading: true,
    totalPages: 0,
    totalItemCount: -1,
    defaultSortBy: ["lineDate"],
    defaultSortDesc: [true],
    contactPersons: [],
    users: [],
    regStatuses: [],
    contactPersonContact: [],
    projects: [],
  }),
  mounted() {
    this.getFilterData();
  },
  watch: {
    $route() {
      this.data = [];
      this.loading = true;
      this.setHeader();
    },
  },
  methods: {
    async fetchData({ data, bottomOfPage }) {
      this.loading = true;

      await complaintApi
        .getComplaints(data)
        .then((response) => {
          if (bottomOfPage) {
            this.data = [...this.data, ...response.items];
          } else {
            this.data = response.items;
          }

          this.totalPages = response.totalPages;
          this.totalItemCount = response.totalItemCount;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getFilterData() {
      complaintApi.getComplaintsFilteroptions().then((response) => {
        this.contactPersons = response.contactPersons;
        this.users = response.users;
        this.regStatuses = response.regStatuses;
        this.contactPersonContact = response.contactPersonContact;
        this.projects = response.projects;
      });
    },
    clickCard(item: any) {
      this.$router.push({ name: "Action", params: { id: item.id } });
    },
    getChips(item: any) {
      var data = [
        {
          title: item.regStatus,
          color: item.color,
        },
      ];
      if (item.isCondominiumComplaint) {
        data.push({
          title: "Sameie",
          color: "",
        });
      }

      return data;
    },
    setHeader() {
      //Ansvarlig har to filter for a begrense ekstern. Her vil ekstern ha et skult filter som bare har contakter i firma

      var isEnquiry = this.$route.name === "Requests";
      this.headers = [
        {
          text: "isEnquiry",
          value: "isEnquiry",
          type: "boolean",
          show: false,
          filterValues: isEnquiry.toString(),
          filterClause: 0,
          filterName: "isEnquiry",
          removeFilter: true,
          removeFromExport: true,
        },
        {
          text: "Id",
          value: "lineId",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "lineId",
          sortIndex: 0,
        },

        {
          text: "Tittel",
          value: "lineName",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "lineName",
          sortIndex: 1,
        },
        {
          text: "Status",
          value: "regStatus",
          type: "int",
          show: true,
          filterValues: [0, 1, 4, 6, 7],
          filterClause: 0,
          filterName: "lineStatus",
          sortable: false,
        },
        {
          text: "Beskrivelse",
          value: "lineDescription",
          type: "string",
          show: false,
          filterValues: "",
          filterClause: 0,
          filterName: "lineDescription",
          removeFilter: true,
        },
        {
          text: "Frist",
          value: "deadLine",
          type: "date",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "deadLine",
          dateType: "DD-MM-YYYY",
        },
        {
          text: "Prosjekt",
          value: "projectName",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "projectOid",
        },
        {
          text: "Leilighetsnummer",
          value: "projSectionApartNum",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "projSectionApartNum",
          sortIndex: 3,
        },
        {
          text: "Ansvarlig",
          value: "contactPersonName",
          type: "string",
          show: true,
          filterValues: this.showAllActions
            ? !this.isTrysilhus
              ? this.contactPersonContactIds
              : ""
            : this.profile.id,
          filterClause: 0,
          filterName: "contactPersonOid",
          disabled: this.showAllActions ? false : true,
          isAssignedTo: true,
        },
        // {
        //   text: "Ansvarlig",
        //   value: "contactPersonName",
        //   type: "string",
        //   show: false,
        //   filterValues: this.showAllActions
        //     ? !this.isTrysilhus
        //       ? this.contactPersonContactIds
        //       : ""
        //     : this.profile.id,
        //   filterClause: 0,
        //   filterName: "contactPersonOid",
        //   removeFilter: true,
        // },
        {
          text: "Sammeie",
          value: "isCondominiumComplaint",
          type: "boolean",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "isCondominiumComplaint",
        },
        {
          text: "Opprettet",
          value: "lineDate",
          type: "date",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "lineDate",
          dateType: "DD-MM-YYYY",
        },
        {
          text: "Navn kunde",
          value: "customerName",
          type: "string",
          show: false,
          filterValues: "",
          filterClause: 0,
          filterName: "customerName",
          removeFilter: true,
        },
        {
          text: "Mobil kunde",
          value: "customerMobile",
          type: "string",
          show: false,
          filterValues: "",
          filterClause: 0,
          filterName: "customerMobile",
          removeFilter: true,
        },
        {
          text: "Adresse kunde",
          value: "customerAddress",
          type: "string",
          show: false,
          filterValues: "",
          filterClause: 0,
          filterName: "customerAddress",
          removeFilter: true,
        },
      ];
    },
    setColor(item) {
      switch (item.lineStatus) {
        case 0:
        case 1:
        case 4:
        case 7:
          this.$set(item, "color", "warning");
          break;
        case 2:
        case 3:
        case 5:
          this.$set(item, "color", "success");
          break;
        case 6:
          this.$set(item, "color", "error");
          break;
      }
    },
    click(item) {
      var isEnquiry = this.$route.name === "Requests";
      if (isEnquiry) {
        this.$router.push({ name: "Request", params: { id: item.id } });
      } else {
        this.$router.push({ name: "Complaint", params: { id: item.id } });
      }
    },
  },

  computed: {
    ...mapGetters({
      isTrysilhus: "shared/isTrysilhus",
      showAll: "tables/showAll",
      profile: "shared/profile",
      contactPersonContactIds: "shared/contactPersonContactIds",
    }),
    showAllActions(): boolean {
      return this.showAll.value;
    },
    requestType(): string {
      return this.$route.name === "Requests" ? "henvendelser" : "reklamasjoner";
    },
    subtitle(): string {
      return this.showAllActions && this.isTrysilhus
        ? `Alle ${this.requestType}`
        : `Dine ${this.requestType}`;
    },
  },
  components: {
    OverviewComponent,
  },
});
</script>
